import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { http } from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await http.post("/newsletter", { email });
      if (data.status === "already_subscribed") {
        toast("You're already subscribed to Fresio insights.");
      } else {
        toast.success("Welcome — you're on the list.");
      }
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#1D3A2F] text-[#FDFBF7] pt-24 pb-10 mt-24"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center mb-14">
          <span
            data-testid="footer-logo"
            className="font-editorial text-4xl text-[#FDFBF7] tracking-tight leading-none"
          >
            Fresio<sup className="text-lg align-super">®</sup>
          </span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-16 border-b border-[#FDFBF7]/10">
          <div className="lg:col-span-5">
            <h2 className="font-editorial text-4xl sm:text-5xl leading-[1.05]">
              Build the next
              <br />
              beauty brand
              <br />
              the world remembers.
            </h2>
            <form onSubmit={subscribe} className="mt-10 flex gap-2 max-w-md">
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 min-w-0 h-12 px-5 rounded-full bg-transparent border border-[#FDFBF7]/25 text-[#FDFBF7] placeholder:text-[#FDFBF7]/50 text-sm focus:outline-none focus:border-[#FDFBF7]/70 transition"
              />
              <button
                data-testid="newsletter-submit"
                type="submit"
                disabled={busy}
                className="shrink-0 h-12 px-5 sm:px-6 rounded-full bg-[#FDFBF7] text-[#1D3A2F] text-[12px] tracking-[0.18em] uppercase font-semibold hover:bg-white transition disabled:opacity-60"
              >
                {busy ? "..." : "Subscribe"}
              </button>
            </form>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-8">
            <FooterCol
              title="Order Sample"
              links={[
                { to: "/order-sample", label: "Face Care Sample" },
                { to: "/order-sample", label: "Hair Care Sample" },
                { to: "/order-sample", label: "Body Care Sample" },
                { to: "/order-sample", label: "Oral Care Sample" },
                { to: "/order-sample", label: "Baby Care Sample" },
              ]}
            />
            <FooterCol
              title="Company"
              links={[
                { to: "/about", label: "About Us" },
                { to: "/manufacturing", label: "Manufacturing" },
                { to: "/pli", label: "PLI Events" },
                { to: "/order-sample", label: "Order Sample" },
                { to: "/contact", label: "Contact" },
              ]}
            />
            <FooterCol
              title="Support"
              links={[
                { to: "/contact", label: "Help Desk" },
                { to: "/contact", label: "Talk to Expert" },
                { to: "/contact", label: "Request Quote" },
              ]}
            />
            <div>
              <p className="text-[11px] tracking-[0.24em] uppercase text-[#FDFBF7]/60 mb-5">
                Contact
              </p>
              <a href="tel:+919274259625" data-testid="footer-phone-1" className="block text-sm leading-relaxed text-[#FDFBF7]/80 hover:text-[#FDFBF7]">
                +91-92742 59625
              </a>
              <a href="tel:+918488000000" data-testid="footer-phone-2" className="block text-sm leading-relaxed text-[#FDFBF7]/80 hover:text-[#FDFBF7]">
                +91-84880 00000
              </a>
              <a
                href="mailto:sales@fresioherbals.com"
                className="text-sm text-[#FDFBF7]/80 hover:text-[#FDFBF7] mt-1 inline-flex items-center gap-1 break-all"
              >
                sales@fresioherbals.com <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
              <p data-testid="footer-address" className="text-sm text-[#FDFBF7]/80 mt-4 leading-relaxed">
                1782, Savli main road,
                <br />
                Near Manjusar GIDC,
                <br />
                Vadodara, Gujarat 391775
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-10 text-xs text-[#FDFBF7]/50">
          <p>© {new Date().getFullYear()} Fresio Herbals. All rights reserved.</p>
          <p className="tracking-[0.18em] uppercase">
            Cosmetic Manufacturing · India · 30+ Countries Served
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <p className="text-[11px] tracking-[0.24em] uppercase text-[#FDFBF7]/60 mb-5">
        {title}
      </p>
      <ul className="space-y-3">
        {links.map((l) => (
          <li key={l.label}>
            <Link
              to={l.to}
              className="text-sm text-[#FDFBF7]/80 hover:text-[#FDFBF7] transition-colors"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
